import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { IdCard, MonitorSmartphone, ShieldCheck } from 'lucide-react'
import { Card, CardHeader, ErrorState, LoadingState, useLocale } from '@hk/ui'
import {
  assignUserRole, endAllSessions, endSession, fetchUser, formatCount, nameIn,
  removeUserRole, setUserEnabled, setUserPreferences, translateError,
  type SessionView, type UserDetailView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { BackBar, Problem, ReasonForm, clock, when } from './StockCounts'
import { Field } from './Users'

/* ============================================================================
   W-121 · USER DETAIL — one person, split down the middle.

   THE SPLIT IS THE SCREEN. Above: what the property system owns, and what an
   administrator must go and fix somewhere else. Below: what is ours, which is
   everything about what this person may do. Pack 5's own note says the read-only block
   is "doing real work" — an administrator trying to correct a spelling learns
   immediately that it has to be fixed at source.

   IN THIS PHASE THE TOP HALF IS NOT READ-ONLY, because there is nothing at source. The
   panel still exists and still says which half is which, so the shape survives the day a
   property system arrives and only the disabled attribute changes. Change Note 05.

   THE SESSIONS PANEL IS THE PART WITH TEETH. Until Sprint 25 the End button pack 5 draws
   had nothing behind it: a JWT is self-contained, so the server that issued it had
   nothing to consult and a handset left in a taxi went on working for twelve hours. It
   now names a row, and ending that row refuses the very next request the device makes.
   ========================================================================= */

export function UserDetail({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [reload, setReload] = useState(0)
  const [settled, setSettled] = useState<UserDetailView | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [said, setSaid] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [disabling, setDisabling] = useState(false)

  const [nameAr, setNameAr] = useState('')
  const [language, setLanguage] = useState('en')
  const [roleId, setRoleId] = useState('')
  const [propertyId, setPropertyId] = useState('')

  const query = useQuery({
    queryKey: ['user', userId, reload],
    queryFn: ({ signal }) => fetchUser(userId, { signal }),
    retry: false,
  })

  const detail = settled ?? query.data

  /* Copied into state on purpose — this screen means to hold what somebody has typed
     against a refetch. Reseeded only when the PERSON changes, never on an ordinary
     refresh, so a half-typed Arabic name does not vanish under them. */
  useEffect(() => {
    if (!detail) return
    setNameAr(detail.person.displayNameAr ?? '')
    setLanguage(detail.person.preferredLanguage)
    setRoleId(detail.person.roles[0]?.roleId ?? '')
    setPropertyId(detail.person.roles[0]?.propertyId ?? detail.properties[0]?.propertyId ?? '')
  }, [detail?.person.userId]) // eslint-disable-line react-hooks/exhaustive-deps

  async function run(what: string, action: () => Promise<UserDetailView>) {
    setBusy(what); setError(null); setSaid(null)
    try {
      // THE ANSWER THE SERVER GAVE IS THE NEW STATE. Refetching against a cache nobody
      // has invalidated is how W-106 spent an afternoon showing "awaiting approval"
      // after the ledger had been written (#77).
      setSettled(await action())
      setSaid(t.usrSaved)
    } catch (caught: unknown) {
      setError(caught)
      // A REFUSAL MEANS THE SCREEN IS NOW WRONG, so go back to the server. The guards
      // here refuse things the screen was offering — the last administrator, your own
      // account — and leaving the stale board up invites a second attempt.
      setSettled(null)
      setReload((n) => n + 1)
    } finally {
      setBusy(null)
    }
  }

  if (query.isLoading && !query.error && !settled) return <LoadingState rows={8} />

  if (query.error && !detail) {
    const translated = translateError(query.error, locale)
    return (
      <div className="flex flex-col gap-3.5">
        <BackBar onClose={onClose} label={t.usrBack} />
        <ErrorState
          body={translated.message}
          onRetry={query.refetch}
          {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
        />
      </div>
    )
  }
  if (!detail) return <LoadingState />

  const person = detail.person
  const live = detail.sessions.filter((s) => s.endedAtUtc === null)

  return (
    <div className="flex flex-col gap-3.5">
      <BackBar onClose={onClose} label={t.usrBack} />

      {error !== null && <Problem error={error} locale={locale} />}

      {/* ── what the property system owns ──────────────────────────────────── */}
      <Card>
        <CardHeader
          // A ReactNode, not a string: `CardHeader.title` takes one, and the name gets
          // a <bdi> of its own — an Arabic name in a header beside Latin text is exactly
          // where the bidi algorithm rearranges things.
          title={<bdi>{nameIn(locale, person.displayName, person.displayNameAr)}</bdi>}
          subtitle={t.usrFromPms}
          icon={<IdCard className="size-4" />}
        />
        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[68ch] text-[12px] leading-relaxed text-muted-foreground">
            {t.usrFromPmsWhy}
          </p>
          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            <Fact label={t.usrColEmployeeNo} value={person.employeeId} tnum />
            <Fact label={t.usrColState} value={standingLabel(person.standing, locale)} />
            <Fact
              label={t.usrColSource}
              value={person.source === 'SYNCED' ? t.usrSourceSynced : t.usrSourceEnteredHere}
            />
            {/* THROUGH THE DICTIONARY, like every other label. This one was written
                straight into the markup and read "Email" on the Arabic screen —
                the same failure as the stock item's unit (#74), and invisible to the
                Arabic guard for the same reason: **a guard over the dictionary cannot
                see a string that was never put in it.** */}
            <Fact label={t.usrEmail} value={detail.email ?? '—'} />
          </div>

          {person.standing === 'DISABLED' && detail.deactivatedAtUtc !== null && (
            <p
              role="status"
              className="rounded-hk border border-border bg-muted/40 px-3 py-2 text-[12.5px]"
            >
              <bdi>
                {t.usrDisabledOn
                  .replace('{when}', when(detail.deactivatedAtUtc, locale))
                  .replace('{reason}', detail.deactivatedReason ?? '')}
              </bdi>
            </p>
          )}
        </div>
      </Card>

      {/* ── what is OURS ───────────────────────────────────────────────────── */}
      <Card>
        <CardHeader title={t.usrOurs} icon={<ShieldCheck className="size-4" />} />
        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[68ch] text-[12px] leading-relaxed text-muted-foreground">
            {t.usrOursWhy}
          </p>

          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            <Field label={t.usrRole}>
              <select
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                aria-label={t.usrRole}
                className="w-full rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              >
                <option value="">—</option>
                {/* THROUGH `nameIn`, like every other piece of reference data. Roles
                    were the last table in the product with no Arabic column, because
                    they had never been drawn on a screen until now. */}
                {detail.roles.map((r) => (
                  <option key={r.roleId} value={r.roleId}>{nameIn(locale, r.name, r.nameAr)}</option>
                ))}
              </select>
            </Field>
            <Field label={t.usrProperty}>
              <select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                aria-label={t.usrProperty}
                className="w-full rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              >
                {detail.properties.map((p) => (
                  <option key={p.propertyId} value={p.propertyId}>
                    {nameIn(locale, p.name, p.nameAr)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t.usrNameAr}>
              <input
                type="text"
                dir="rtl"
                value={nameAr}
                onChange={(e) => setNameAr(e.target.value)}
                aria-label={t.usrNameAr}
                className="w-full rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              />
            </Field>
            <Field label={t.usrLanguage}>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                aria-label={t.usrLanguage}
                className="w-full rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              >
                <option value="en">{t.usrEnglish}</option>
                <option value="ar">{t.usrArabic}</option>
              </select>
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void run('saving', async () => {
                if (roleId !== '' && propertyId !== '')
                  await assignUserRole(userId, { propertyId, roleId })
                return setUserPreferences(userId, {
                  displayNameAr: nameAr.trim() === '' ? null : nameAr.trim(),
                  preferredLanguage: language,
                })
              })}
              className="rounded-hk bg-[var(--hk-primary)] px-3 py-1.5 text-xs font-medium
                         text-white disabled:opacity-60 cursor-pointer"
            >
              {busy === 'saving' ? t.usrSaving : t.usrSave}
            </button>

            {person.roles.length > 0 && (
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void run('removing', () =>
                  removeUserRole(userId, person.roles[0]!.propertyId))}
                className="rounded-hk border border-border px-3 py-1.5 text-xs font-medium
                           hover:bg-muted disabled:opacity-60 cursor-pointer"
              >
                {t.usrRemoveRole}
              </button>
            )}

            {said !== null && (
              <span role="status" className="text-[12px] text-muted-foreground">{said}</span>
            )}

            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                if (person.standing === 'DISABLED')
                  void run('enabling', () => setUserEnabled(userId, {
                    enabled: true, reason: 'Re-enabled from the users screen.',
                  }))
                else setDisabling(true)
              }}
              className="ms-auto text-[11.5px] text-muted-foreground underline
                         underline-offset-2 disabled:opacity-60 cursor-pointer"
            >
              {person.standing === 'DISABLED' ? t.usrEnable : t.usrDisable}
            </button>
          </div>

          {disabling && (
            <>
              {/* SAID BEFORE THE ACT, not after it. Disabling somebody ends every
                  session they hold on their next tap — which is the point, and is
                  also not what most people expect a "disable" checkbox to do. */}
              <p className="max-w-[68ch] text-[12.5px] text-[#b45309] dark:text-[#fbbf24]">
                {t.usrDisableWarning}
              </p>
              <ReasonForm
                label={t.usrDisableReason}
                confirm={t.usrDisableConfirm}
                busy={busy !== null}
                onCancel={() => setDisabling(false)}
                onSubmit={(reason) => {
                  setDisabling(false)
                  void run('disabling', () =>
                    setUserEnabled(userId, { enabled: false, reason }))
                }}
              />
            </>
          )}
        </div>
      </Card>

      {/* ── sessions ───────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          title={t.usrSessions}
          icon={<MonitorSmartphone className="size-4" />}
          actions={live.length > 0 ? (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void run('ending-all', () => endAllSessions(userId))}
              className="rounded-hk border border-border px-2.5 py-1 text-[11.5px]
                         font-medium hover:bg-muted disabled:opacity-60 cursor-pointer"
            >
              {t.usrEndAll}
            </button>
          ) : undefined}
        />
        <div className="p-3.5 pb-0">
          <p className="max-w-[68ch] text-[12px] leading-relaxed text-muted-foreground">
            {t.usrSessionsWhy}
          </p>
        </div>
        {detail.sessions.length === 0 ? (
          <p className="p-3.5 text-[12.5px] text-muted-foreground">{t.usrNoSessions}</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                  <th className="px-3 py-2 text-start font-medium">{t.usrColDevice}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.usrColSignedIn}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.usrColExpires}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.usrColActivity}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {detail.sessions.map((session) => (
                  <SessionRow
                    key={session.sessionId}
                    session={session}
                    locale={locale}
                    busy={busy !== null}
                    onEnd={() => void run('ending', () => endSession(session.sessionId))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── activity ───────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader title={t.usrActivity} subtitle={t.usrActivityWindow} />
        <div className="flex flex-col gap-3 p-3.5">
          <div className="grid gap-2.5 sm:grid-cols-3">
            <Fact
              label={t.usrTasksCompleted}
              value={formatCount(detail.activity.tasksCompleted, locale)} big tnum
            />
            <Fact
              label={t.usrInspectionsFailed}
              value={formatCount(detail.activity.inspectionsFailed, locale)} big tnum
            />
            <Fact
              label={t.usrRequestsFulfilled}
              value={formatCount(detail.activity.requestsFulfilled, locale)} big tnum
            />
          </div>
          {/* WHY THERE IS NO PERCENTAGE HERE, said on the screen rather than left as a
              gap somebody fills in with a worse one later. */}
          <p className="max-w-[68ch] text-[11.5px] leading-relaxed text-muted-foreground">
            {t.usrNoRate}
          </p>
        </div>
      </Card>
    </div>
  )
}

function SessionRow({
  session, locale, busy, onEnd,
}: { session: SessionView; locale: 'en' | 'ar'; busy: boolean; onEnd: () => void }) {
  const t = STRINGS[locale]
  const ended = session.endedAtUtc !== null

  return (
    <tr className={`border-b border-border/60 last:border-0 ${ended ? 'opacity-60' : ''}`}>
      <td className="px-3 py-2">
        {/* NEVER A GUESS. A null code is drawn as "Unknown device", because the wrong
            label on this row ends the wrong person's shift. */}
        <bdi className="font-medium">{deviceLabel(session.deviceLabel, locale)}</bdi>
        {session.isYours && !ended && (
          <span className="ms-2 rounded-hk border border-[#93c5fd] bg-[#eff6ff] px-1.5
                           py-0.5 text-[11px] text-[#1d4ed8]
                           dark:border-[#1e40af] dark:bg-[#0f1c33] dark:text-[#93c5fd]">
            {t.usrThisDevice}
          </span>
        )}
        {ended && (
          <span className="block text-[11px] text-muted-foreground">
            {endedLabel(session.endedReason, locale)}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">{when(session.issuedAtUtc, locale)}</td>
      <td className="px-3 py-2 text-muted-foreground">{clock(session.expiresAtUtc, locale)}</td>
      <td className="px-3 py-2 text-muted-foreground">{when(session.lastSeenAtUtc, locale)}</td>
      <td className="px-3 py-2 text-end">
        {ended ? (
          <span className="text-[11.5px] text-muted-foreground">{t.usrEnded}</span>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={onEnd}
            className="rounded-hk border border-border px-2.5 py-1 text-[11.5px]
                       font-medium hover:bg-muted disabled:opacity-60 cursor-pointer"
          >
            {t.usrEnd}
          </button>
        )}
      </td>
    </tr>
  )
}

function Fact({
  label, value, big = false, tnum = false,
}: { label: string; value: string; big?: boolean; tnum?: boolean }) {
  return (
    <div className="rounded-hk border border-border p-2.5">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`${big ? 'text-[19px]' : 'text-[13px]'} font-medium ${tnum ? 'tabular-nums' : ''}`}>
        <bdi>{value}</bdi>
      </div>
    </div>
  )
}

function standingLabel(standing: string, locale: 'en' | 'ar'): string {
  const t = STRINGS[locale]
  return standing === 'ACTIVE' ? t.usrActive
    : standing === 'NO_ROLE' ? t.usrNoRole
    : t.usrDisabled
}

/**
 * THE DEVICE, IN THE READER'S LANGUAGE.
 *
 * The server sends a CODE — ANDROID, CHROME, HANDSET_APP — and the words live here, in
 * both dictionaries. It sent the English phrase until Sprint 25 and it sat in Latin
 * script in the middle of the Arabic sessions panel, which is the same failure as the
 * stock item's unit (#74) and was found the same way, by looking at the render.
 *
 * **EXPORTED SINCE SPRINT 31.** W-124 draws the same codes — a session that was ended
 * names its device, and a refused sign-in says which kind of thing it came from — and a
 * second copy of this switch on that screen would be two lists that drift. The device code
 * is the thing #82 was about; having translated it once, the answer is to share the
 * function rather than to translate it again.
 *
 * TWO DIFFERENT KINDS OF "WE DO NOT KNOW", and they are not the same sentence. NULL
 * means the User-Agent was unrecognisable — "Unknown device", which is true. An
 * unrecognised CODE means a newer server knows something this build does not, so the
 * code itself is drawn: visibly untranslated, and reportable, exactly as `nameIn` falls
 * back to the Latin name rather than to a blank.
 */
export function deviceLabel(code: string | null, locale: 'en' | 'ar'): string {
  const t = STRINGS[locale]
  if (code === null) return t.usrUnknownDevice
  switch (code) {
    case 'HANDSET_APP': return t.usrDeviceHandsetApp
    case 'ANDROID': return t.usrDeviceAndroid
    case 'IPHONE': return t.usrDeviceIphone
    case 'IPAD': return t.usrDeviceIpad
    case 'EDGE': return t.usrDeviceEdge
    case 'FIREFOX': return t.usrDeviceFirefox
    case 'CHROME': return t.usrDeviceChrome
    case 'SAFARI': return t.usrDeviceSafari
    default: return code
  }
}

function endedLabel(reason: string | null, locale: 'en' | 'ar'): string {
  const t = STRINGS[locale]
  switch (reason) {
    case 'SIGNED_OUT': return t.usrEndedSignedOut
    case 'ENDED_BY_ADMIN': return t.usrEndedByAdmin
    case 'USER_DISABLED': return t.usrEndedDisabled
    case 'ALL_ENDED': return t.usrEndedAll
    default: return t.usrEnded
  }
}
